import React, { useState } from 'react'
import { Avatar, Checkbox, Col, DatePicker, Form, Input, Row } from 'antd'
import FileUploader from 'helper/FileUploader'
import GenderSelect from 'helper/GenderSelect'
import AllergyList from 'helper/AllergyList'
import RelationsList from 'helper/RelationsList'
import PhoneInput from 'react-phone-input-2'
import MapModal from 'helper/Map/MapModal'
import { validateAlphabets } from 'helper/utility'
import { CreateAdultPayload } from 'types/family/adult'
import { Address, Allergy, Phone } from 'types/globalTypes'
import { useCreateAdult } from 'api/family/adult'
import ButtonSpinner from 'components/global/ButtonSpinner'
import moment from 'moment'
import 'react-phone-input-2/lib/style.css'

interface Props {
  familyId: string
  prefillEmail?: string
  onSuccess: (adultId: string) => void
  onBack: () => void
  externalLoading?: boolean
}

const DropInAddAdultForm: React.FC<Props> = ({ familyId, prefillEmail, onSuccess, onBack, externalLoading }) => {
  const [form] = Form.useForm()
  const [avatar, setAvatar] = useState<string>('')
  const [phone, setPhone] = useState<Phone>({ dialCode: '+1', isoCode: 'US', number: '' })
  const [mapModalVisible, setMapModalVisible] = useState(false)
  const [mapModalData, setMapModalData] = useState<Address | undefined>()
  const [isEmergencyContact, setIsEmergencyContact] = useState(false)
  const [allergies, setAllergies] = useState<Allergy[]>([])

  const { mutate: createAdult, isLoading } = useCreateAdult()

  const onFinish = (values: CreateAdultPayload) => {
    const payload: CreateAdultPayload = {
      ...values,
      dob: values.dob ? moment(values.dob).format('YYYY-MM-DD') : undefined,
      phone: phone.number && phone.number.length > 4 ? phone : undefined,
      address: mapModalData,
      isEmergencyContact,
      allergies,
    }
    createAdult(
      { familyId, payload },
      { onSuccess: (data: any) => onSuccess(data._id) },
    )
  }

  const busy = isLoading || !!externalLoading

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
      initialValues={{ email: prefillEmail }}
    >
      {/* Photo */}
      <Row gutter={24} align="middle" className="mb-10px">
        <Col span={4}><Avatar size={64} src={avatar || undefined} /></Col>
        <Col span={18}>
          <Form.Item name="photoId">
            <FileUploader setAvatar={setAvatar} name="drop-in-adult" form={form} fileId={undefined} />
          </Form.Item>
        </Col>
      </Row>

      <p className="text-12 w-400 mb-10px">Adults who are allowed to pick up child(ren).</p>

      {/* First + Middle */}
      <Row gutter={24}>
        <Col span={12}>
          <label className="input-label required-input">First Name</label>
          <Form.Item name="firstName" rules={[{ required: true, message: 'Required' }, { validator: validateAlphabets }]}>
            <Input placeholder="First name" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <label className="input-label">Middle Name</label>
          <Form.Item name="middleName">
            <Input placeholder="Middle name" />
          </Form.Item>
        </Col>
      </Row>

      {/* Last + Preferred */}
      <Row gutter={24}>
        <Col span={12}>
          <label className="input-label required-input">Last Name</label>
          <Form.Item name="lastName" rules={[{ required: true, message: 'Required' }, { validator: validateAlphabets }]}>
            <Input placeholder="Last name" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <label className="input-label">Preferred Name</label>
          <Form.Item name="nickName">
            <Input placeholder="Preferred name" />
          </Form.Item>
        </Col>
      </Row>

      {/* Relationship */}
      <Row>
        <Col span={24}>
          <label className="input-label required-input">Relationship to kids</label>
          <Form.Item name="relationId" className="form-control" rules={[{ required: true, message: 'Required' }]}>
            <RelationsList form={form} relationId={undefined} data={undefined} />
          </Form.Item>
        </Col>
      </Row>

      {/* Gender */}
      <Row>
        <Col span={24}>
          <label className="input-label">Gender</label>
          <Form.Item name="gender">
            <GenderSelect form={form} data={undefined} />
          </Form.Item>
        </Col>
      </Row>

      {/* DOB */}
      <Row>
        <Col span={24}>
          <label className="input-label">Date of Birth</label>
          <Form.Item name="dob">
            <DatePicker className="selector w-full" />
          </Form.Item>
        </Col>
      </Row>

      {/* Email */}
      <Row>
        <Col span={24}>
          <label className="input-label required-input">Email</label>
          <Form.Item name="email" rules={[{ required: true, message: 'Required' }, { type: 'email', message: 'Invalid email' }]}>
            <Input placeholder="example@gmail.com" />
          </Form.Item>
        </Col>
      </Row>

      {/* Phone */}
      <Row>
        <Col span={24}>
          <label className="input-label">Phone number</label>
          <Form.Item name="phone" className="phone-number-form">
            <PhoneInput
              country={phone?.isoCode?.toLowerCase() || 'us'}
              onChange={(value: string, data: any) => {
                setPhone({
                  dialCode: '+' + data?.dialCode,
                  number: '+' + value,
                  isoCode: data.countryCode.toUpperCase(),
                })
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Address */}
      <Row>
        <Col span={24}>
          <label className="input-label">Home address</label>
          <Form.Item>
            <Input
              placeholder="Location"
              readOnly
              onClick={() => setMapModalVisible(true)}
              value={mapModalData ? `${mapModalData.city}, ${mapModalData.state}` : ''}
            />
            {mapModalVisible && (
              <MapModal
                setMapModalVisible={setMapModalVisible}
                setMapModalData={setMapModalData}
                mapData={mapModalData}
              />
            )}
          </Form.Item>
        </Col>
      </Row>

      {/* Allergies */}
      <Row>
        <Col span={24}>
          <label className="input-label">Allergies</label>
          <Form.Item name="allergies" className="form-control allergies-field">
            <AllergyList form={form} setAllergies={setAllergies} data={[]} />
          </Form.Item>
        </Col>
      </Row>

      {/* Medical Notes */}
      <Row>
        <Col span={24}>
          <label className="input-label">Medical Notes</label>
          <Form.Item name="medicalNotes">
            <Input placeholder="Ex.: Asthma, Lactose intolerant" />
          </Form.Item>
        </Col>
      </Row>

      {/* Emergency Contact */}
      <Row className="mb-16px">
        <Col span={24}>
          <Form.Item name="isEmergencyContact" valuePropName="checked">
            <Checkbox
              checked={isEmergencyContact}
              onChange={e => setIsEmergencyContact(e.target.checked)}
            >
              <span className="w-600 text-12">Emergency Contact</span>
            </Checkbox>
          </Form.Item>
        </Col>
      </Row>

      <div className="flex gap-10px mt-10px">
        <button type="submit" className="btn-prim" disabled={busy}>
          {busy ? <ButtonSpinner size={20} color="#fff" /> : 'Create adult & Check Out'}
        </button>
        <button type="button" className="btn-borderless" onClick={onBack} disabled={busy}>
          Back
        </button>
      </div>
    </Form>
  )
}

export default DropInAddAdultForm
